import { useState, useEffect } from "react";
import Badge from "../shared-components/Badge";
import { useGetAllBlueprints } from "../api/BlueprintsApi";
import { BlueprintMinimalInfo } from "../api/Blueprints";
import { CompanyFilterInfoApi } from "../api/Companies";

import { useGetSubCompanyInfoList } from "../api/CompaniesApi";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";

interface TableProps {
  data: CompanyFilterInfoApi[] | undefined;
  header: Array<string>;
  showSubcompanies?: boolean;
  isLoading: boolean;
  onClick?: (companyUUID: string, parentCompanyUUID?: string) => void;
}

const colorByIndex = (index: number): string => {
  // WARNING: For colors we don't want to validate names because they can get updated
  // the colors will be assign by index to ensure consistency.
  switch (index) {
    case 0:
      return "green";
    case 1:
      return "blue";
    default:
      return "yellow";
  }
};

type indexedBlueprintItem = {
  idx: number;
  item: BlueprintMinimalInfo;
};

const Table = ({
  data,
  header,
  onClick,
  isLoading,
  showSubcompanies = true,
}: TableProps) => {
  const {
    data: blueprintsListData,
    mutate: getAllBlueprints,
    error: errorBlueprints,
  } = useGetAllBlueprints();

  const [queryCompanyUUID, setQueryCompanyUUID] = useState("");
  const [companiesList, setCompaniesList] = useState<string[]>([]);

  const [blueprintsMap, setBlueprintsMap] = useState<
    Map<string, indexedBlueprintItem>
  >(new Map());
  useEffect(() => {
    getAllBlueprints();
  }, [getAllBlueprints]);

  useEffect(() => {
    if (blueprintsListData) {
      const map = new Map<string, indexedBlueprintItem>();
      blueprintsListData.map((item, idx) =>
        map.set(item.blueprint_uuid ?? "", { idx, item })
      );
      setBlueprintsMap(map);
    }
  }, [blueprintsListData]);

  function getCheckNameByUUID(uuid: string): string {
    const defaultName = "loading...";
    const foundCheck = blueprintsMap.get(uuid);
    return foundCheck?.item.name || defaultName;
  }

  function colorByUuid(uuid: string): string {
    const index = blueprintsMap.get(uuid)?.idx ?? -1;
    return colorByIndex(index);
  }

  const [tableData, setTableData] = useState<CompanyFilterInfoApi[]>([]);

  useEffect(() => {
    if (data) setTableData(data);
  }, [data]);

  useEffect(() => {
    if (!queryCompanyUUID) return;

    if (companiesList.some((pivot) => pivot === queryCompanyUUID)) return;

    setCompaniesList([...companiesList, queryCompanyUUID]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryCompanyUUID]);

  function loadSubcompanies(companyUUID: string | undefined) {
    if (!companyUUID) return;
    if (isOnCompaniesList(companyUUID)) {
      setCompaniesList(companiesList.filter((pivot) => pivot !== companyUUID));
      setQueryCompanyUUID("");
      return;
    }
    setQueryCompanyUUID(companyUUID);
  }

  function isOnCompaniesList(companyUUID: string | undefined): boolean {
    if (!companyUUID) return false;
    return companiesList.some((pivot) => pivot === companyUUID);
  }
  if (errorBlueprints)
    return (
      <ErrorMessageContainer message="Error loading check types for companies" />
    );
  return (
    <>
      <div>
        <div className="flex text-gray-500 rounded-t-xl border shadow shadow-gray-300 bg-gray-50">
          {header.map((header) => (
            <div
              className="flex rounded-t-xl items-center p-3 text-xs leading-4 font-medium bg-gray-50 uppercase flex-1"
              key={header}
            >
              {header}
            </div>
          ))}
        </div>
        <div className="bg-white border-l border-r rounded-b-xl shadow shadow-gray-300">
          {isLoading ? <LoadingSpinner /> : ""}
          {tableData.length === 0 && !isLoading ? (
            <div className="text-sm flex flex-col justify-center items-center border-b py-4 min-h-96">
              <div className="font-bold">No Sub Companies Added</div>
              <div className="text-gray-400 pt-4">
                No sub-companies to display yet. They'll show up here once
                available
              </div>
            </div>
          ) : (
            tableData.map(
              ({ company_uuid, parent_company_uuid, name, blueprint }, i) => (
                <div key={company_uuid || `company-${i}`}>
                  <div className="flex border-b text-xs leading-4 font-normal py-4 hover:bg-gray-50">
                    <div
                      className="flex px-3 flex-1 font-semibold cursor-pointer truncate text-ellipsis w-32"
                      onClick={() => {
                        if (company_uuid)
                          onClick?.(company_uuid, parent_company_uuid);
                      }}
                    >
                      {name}
                    </div>
                    <div className="flex flex-wrap px-3 flex-1 gap-2">
                      {blueprint &&
                        blueprint.map((blueprintUUID) => (
                          // Note: Will need filtering if we don't want multiple checks of same name
                          <Badge
                            key={blueprintUUID}
                            color={colorByUuid(blueprintUUID)}
                          >
                            {getCheckNameByUUID(blueprintUUID)}
                          </Badge>
                        ))}
                    </div>
                    {showSubcompanies && (
                      <div
                        className="w-5 mr-4 text-gray-400"
                        onClick={() => loadSubcompanies(company_uuid)}
                      >
                        {isOnCompaniesList(company_uuid) ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </div>
                    )}
                  </div>
                  {isOnCompaniesList(company_uuid) && (
                    <SubCompaniesRow
                      companyUUID={company_uuid}
                      colorByUuid={colorByUuid}
                      getCheckNameByUUID={getCheckNameByUUID}
                      onClick={onClick}
                    />
                  )}
                </div>
              )
            )
          )}
        </div>
      </div>
    </>
  );
};

function SubCompaniesRow({
  companyUUID,
  colorByUuid,
  getCheckNameByUUID,
  onClick,
}: {
  companyUUID: string | undefined;
  colorByUuid: (uuid: string) => string;
  getCheckNameByUUID: (uuid: string) => string;
  onClick?: (companyUUID: string, parentCompanyUUID?: string) => void;
}) {
  const {
    data: subcompaniesData,
    mutate: getSubcompanies,
    isLoading: isLoadingSubcompanies,
  } = useGetSubCompanyInfoList();

  useEffect(() => {
    if (!companyUUID) return;
    getSubcompanies({ page: 1, size: 25, company_uuid: companyUUID });
  }, [companyUUID, getSubcompanies]);
  if (!companyUUID) return <></>;
  if (isLoadingSubcompanies)
    return (
      <div>
        <LoadingSpinner className="w-full border-b p-4 relative" />
      </div>
    );
  if (!subcompaniesData?.items) return <></>;

  const rows = subcompaniesData?.items.filter(
    (pivot) => pivot.company_uuid !== companyUUID
  );

  if (rows.length === 0)
    return (
      <div className="text-sm flex justify-center border-b py-4">
        No Subcompanies Added
        <div>
          No sub-companies to display yet. They'll show up here once available
        </div>
      </div>
    );

  return (
    <div>
      {rows.map(({ company_uuid: subCompanyUuid, name, blueprint }, i) => (
        <>
          <div
            className="flex text-xs  border-b leading-4 font-normal py-4 hover:bg-gray-50 border-l-2 border-l-green-500"
            key={subCompanyUuid || `company-${i}`}
            onClick={() => {
              if (subCompanyUuid) {
                onClick?.(subCompanyUuid, companyUUID);
              }
            }}
          >
            <div className="flex px-3 flex-1 font-semibold cursor-pointer">
              {name}
            </div>
            <div className="flex px-3 flex-1 gap-2">
              {blueprint &&
                blueprint.map((blueprintUUID) => (
                  // Note: Will need filtering if we don't want multiple checks of same name
                  <Badge key={blueprintUUID} color={colorByUuid(blueprintUUID)}>
                    {getCheckNameByUUID(blueprintUUID)}
                  </Badge>
                ))}
            </div>
          </div>
        </>
      ))}
    </div>
  );
}

export default Table;
